import { Button } from 'admin/components/atoms/Buttons';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Labeled from 'admin/components/atoms/Labeled';
import TextBox from 'admin/components/atoms/TextBox';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  BooleanParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { getQueryParamDefaultValues } from '../../../utils/use-query-params';
import { createColorCodedOptions } from '../../atoms/ColorCodedOption';
import Select from '../../atoms/Select';
import LabeledSelect from '../../molecules/LabeledSelect';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

type BillsQueryParamConfig = typeof queryParamConfigs;
export type BillsQueryParams = {
  [key in keyof BillsQueryParamConfig]: BillsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

type FormFilterParams = Omit<BillsQueryParams, 'from' | 'to'> & {
  date_range: {
    created_from: moment.Moment | null;
    created_to: moment.Moment | null;
  };
};

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  bill_id: withDefault(StringParam, ''),
  created_from: withDefault(MomentParam, null),
  created_to: withDefault(MomentParam, null),
  issuer_shop: withDefault(StringParam, ''),
  enable_or_disable: withDefault(BooleanParam, null),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const Wrapper = styled.div`
  ${Button} {
    margin: 5px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${Labeled} {
    display: flex;
    margin: 10px;
    align-items: center;
    gap: 10px;
  }

  ${Select as any} {
    width: 300px;
  }

  ${TextBox} {
    width: 300px;
  }
`;

const LabelText = styled.div``;

const STATUS_OPTIONS = [
  {
    label: '指定なし',
    value: null,
    bgColor: '#D0D0D0',
  },
  {
    label: '有効',
    value: true,
    bgColor: '#21BF25',
  },
  {
    label: '無効',
    value: false,
    bgColor: '#D0D0D0',
  },
];

const formatOptionLabel = createColorCodedOptions(STATUS_OPTIONS);

const FilterForm = ({
  applyFilters,
}: {
  applyFilters: (params: BillsQueryParams & { private_money_id: string }) => void;
}) => {
  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const formDefaultValues = {
    bill_id: query.bill_id ?? '',
    date_range: {
      created_from: query.created_from ?? null,
      created_to: query.created_to ?? null,
    },
    issuer_shop: query.issuer_shop ?? '',
    enable_or_disable: query.enable_or_disable ?? null,
  };

  const { handleSubmit, control, reset, register } = useForm<FormFilterParams>({
    defaultValues: formDefaultValues,
  });

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  useEffect(() => {
    if (userPrivateMoney) {
      applyFilters({
        ...query,
        private_money_id: userPrivateMoney.id,
      });

      reset(formDefaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivateMoney, query]);

  const onConfirm = (data) => {
    const { date_range, ...rest } = data;
    setQuery({ ...date_range, ...rest });
  };

  const clearFilters = () => {
    const { created_from, created_to, ...rest } = queryParamDefaultValues;

    setQuery({ ...queryParamDefaultValues });
    reset({
      date_range: {
        created_from,
        created_to,
      },
      ...rest,
    });
  };

  return (
    <Wrapper>
      <SelectedPrivateMoneyDisplay />
      <FormWrapper>
        <LabeledTextBox labelText="ID" {...register('bill_id')} />
        <Controller
          name="date_range"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Labeled labelText={<LabelText>発行日時</LabelText>}>
              <DateTimeRange
                dateRange={{ from: value?.created_from, to: value?.created_to }}
                setDateRange={({ from, to }) =>
                  onChange({ created_from: from ?? null, created_to: to ?? null })
                }
                validTypeFrom="invalidAfterToday"
                validTypeTo="invalidAfterToday"
                wrapperStyles={{ marginBottom: 0 }}
                dateTimeProps={{ toProps: { showIcon: true }, fromProps: { showIcon: true } }}
              />
            </Labeled>
          )}
        />
        <LabeledTextBox labelText={<LabelText>発行店舗</LabelText>} {...register('issuer_shop')} />
        <Controller
          name="enable_or_disable"
          control={control}
          render={({ field: { value, onChange } }) => (
            <LabeledSelect
              labelText={<LabelText>状態</LabelText>}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((opt) => opt.value === value)}
              isMulti={false}
              onChange={(opt) => onChange(opt?.value ?? null)}
              formatOptionLabel={formatOptionLabel}
            />
          )}
        />
      </FormWrapper>
      <Button variant="neutral" onClick={clearFilters}>
        クリア
      </Button>
      <Button onClick={handleSubmit(onConfirm)}>検索</Button>
    </Wrapper>
  );
};

export default FilterForm;
